import { ConcurrencyPool, recommendedConcurrency } from './ConcurrencyPool'
import { isMobile, onResumeFromBackground, ScreenWakeGuard, warnBeforeUnload } from './mobile'
import { uploadMultipart } from './MultipartUploader'
import { deleteSession, listSessions, matchesFile, pruneStale } from './persistence'
import { uploadSingle } from './SingleUploader'
import type { UploadRequestFile, UploadTransport } from './transport'
import type {
  QuotaSnapshot,
  ResumableUpload,
  UploadItem,
  UploadItemStatus,
  UploadTicket,
} from './types'

export interface UploadManagerEvents {
  onItemChange: (item: UploadItem) => void
  onQuotaChange?: (quota: QuotaSnapshot | null) => void
  onBatchError?: (message: string) => void
}

interface Tracked {
  item: UploadItem
  controller: AbortController
}

/**
 * Orquesta la cola de subidas de un proyecto.
 *
 * Responsabilidades que NO delega:
 *   - pedir los tickets en un solo lote (latencia Lima↔US East),
 *   - limitar cuántos archivos suben a la vez según el dispositivo,
 *   - mantener la pantalla encendida y reanudar al volver de segundo plano,
 *   - avisar antes de cerrar con subidas activas.
 *
 * No sabe nada de React: se comunica por callbacks. La capa de estado
 * (useUploadQueue) la envuelve.
 */
export class UploadManager {
  private readonly tracked = new Map<string, Tracked>()

  private readonly pool: ConcurrencyPool

  private readonly partConcurrency: number

  private readonly wakeGuard = new ScreenWakeGuard()

  private disposeVisibility: (() => void) | null = null

  private disposeUnload: (() => void) | null = null

  private nextId = 0

  constructor(
    private readonly projectId: string,
    private readonly transport: UploadTransport,
    private readonly events: UploadManagerEvents,
  ) {
    const limits = recommendedConcurrency()
    this.pool = new ConcurrencyPool(limits.files)
    this.partConcurrency = limits.parts
  }

  /** Limpia sesiones de IndexedDB demasiado viejas para reanudarse. */
  static async prune(): Promise<void> {
    // Quien aborta el multipart es nuestro backend a las ~7 h, no la regla de
    // 7 días de R2. El porqué, y por qué aquí bastan 24 h, está en `pruneStale`.
    await pruneStale()
  }

  async enqueue(files: File[]): Promise<void> {
    if (files.length === 0) return

    const entries = files.map((file) => {
      const item: UploadItem = {
        id: `u${this.nextId++}`,
        file,
        filename: file.name,
        sizeBytes: file.size,
        status: 'preparing' satisfies UploadItemStatus,
        bytesSent: 0,
        error: null,
        assetId: null,
        sessionId: null,
        strategy: null,
        completedParts: 0,
        totalParts: 0,
      }

      const entry: Tracked = { item, controller: new AbortController() }
      this.tracked.set(item.id, entry)
      this.emit(item)

      return entry
    })

    this.attachLifecycleGuards()

    let tickets: UploadTicket[]

    try {
      // UNA llamada para todo el lote. Con 400 fotos, una petición por archivo
      // serían ~32 s sólo de latencia antes de mover un byte.
      const payload: UploadRequestFile[] = entries.map(({ item }) => ({
        filename: item.filename,
        size_bytes: item.sizeBytes,
        mime_type: item.file?.type || 'application/octet-stream',
      }))

      const response = await this.transport.issueTickets(payload)
      tickets = response.tickets
      this.events.onQuotaChange?.(response.quota)
    } catch (error) {
      // El lote se autoriza o se rechaza entero: la reserva de cuota es
      // atómica, así que no tiene sentido dejar unos en cola y otros no.
      const message = error instanceof Error ? error.message : 'No se pudo iniciar la subida.'

      for (const { item } of entries) {
        this.update(item.id, { status: 'failed', error: message })
      }

      this.events.onBatchError?.(message)
      this.releaseLifecycleGuardsIfIdle()

      return
    }

    await Promise.all(
      entries.map((entry, index) => {
        const ticket = tickets[index]

        if (!ticket) {
          this.update(entry.item.id, { status: 'failed', error: 'Falta el ticket de subida.' })

          return Promise.resolve()
        }

        return this.pool.run(() => this.runOne(entry, ticket)).catch(() => undefined)
      }),
    )

    this.releaseLifecycleGuardsIfIdle()
  }

  /**
   * Subidas interrumpidas que se pueden continuar.
   *
   * Se cruzan las sesiones guardadas en IndexedDB con el estado real en el
   * servidor: la sesión pudo expirar, completarse desde otra pestaña o
   * abortarse. Las que ya no sirven se limpian aquí mismo para que la lista no
   * ofrezca reanudar algo imposible.
   */
  async listResumable(): Promise<ResumableUpload[]> {
    const stored = (await listSessions()).filter((s) => s.projectId === this.projectId)
    const resumable: ResumableUpload[] = []

    for (const session of stored) {
      try {
        const state = await this.transport.getSession(session.sessionId)

        if (!state.resumable) {
          await deleteSession(session.sessionId)
          continue
        }

        resumable.push({
          sessionId: session.sessionId,
          assetId: session.assetId,
          projectId: session.projectId,
          filename: session.filename,
          sizeBytes: session.fileSize,
          fileLastModified: session.fileLastModified,
          partSize: session.partSize,
          totalParts: session.totalParts,
          completedParts: session.parts.length,
        })
      } catch {
        // 404 o error de red: si la sesión ya no existe en el servidor, la
        // entrada local sólo puede confundir.
        await deleteSession(session.sessionId)
      }
    }

    return resumable
  }

  /**
   * Continúa una subida interrumpida.
   *
   * El navegador no conserva el File entre recargas, así que el usuario tiene
   * que reseleccionarlo. Se comprueba que sea el mismo (tamaño y fecha de
   * modificación) antes de reutilizar los ETags guardados: mezclar partes de
   * dos ficheros distintos produciría un objeto corrupto que además pasaría la
   * verificación de tamaño del servidor.
   */
  async resume(resumable: ResumableUpload, file: File): Promise<void> {
    const persisted = await listSessions().then((all) =>
      all.find((s) => s.sessionId === resumable.sessionId),
    )

    if (!persisted || !matchesFile(persisted, file)) {
      throw new Error(
        'El archivo elegido no coincide con la subida interrumpida. ' +
          'Selecciona exactamente el mismo archivo.',
      )
    }

    const item: UploadItem = {
      id: `u${this.nextId++}`,
      file,
      filename: file.name,
      sizeBytes: file.size,
      status: 'uploading',
      bytesSent: 0,
      error: null,
      assetId: resumable.assetId,
      sessionId: resumable.sessionId,
      strategy: 'multipart',
      completedParts: resumable.completedParts,
      totalParts: resumable.totalParts,
    }

    const entry: Tracked = { item, controller: new AbortController() }
    this.tracked.set(item.id, entry)
    this.emit(item)
    this.attachLifecycleGuards()

    // Se reconstruye el ticket a partir de la sesión: no se piden tickets
    // nuevos, porque eso crearía otro asset y duplicaría la reserva de cuota.
    const ticket: UploadTicket = {
      asset_id: resumable.assetId,
      session_id: resumable.sessionId,
      filename: file.name,
      strategy: 'multipart',
      expires_at: '',
      multipart: {
        upload_id: null,
        part_size_bytes: resumable.partSize,
        total_parts: resumable.totalParts,
        parts: [],
        next_part_cursor: 1,
      },
    }

    await this.pool.run(() => this.runOne(entry, ticket)).catch(() => undefined)
    this.releaseLifecycleGuardsIfIdle()
  }

  cancel(itemId: string): void {
    const entry = this.tracked.get(itemId)
    if (!entry) return

    entry.controller.abort()
    this.update(itemId, { status: 'canceled' })

    // Abortar en el servidor libera la reserva de cuota y, en multiparte,
    // descarta las partes ya subidas — que en R2 se cobran hasta que se
    // abortan o las barre una regla de lifecycle.
    if (entry.item.sessionId !== null) {
      void this.transport.abortUpload(entry.item.sessionId).catch(() => undefined)
      void deleteSession(entry.item.sessionId)
    }

    this.releaseLifecycleGuardsIfIdle()
  }

  cancelAll(): void {
    for (const id of this.tracked.keys()) {
      this.cancel(id)
    }
  }

  dispose(): void {
    this.cancelAll()
    this.disposeVisibility?.()
    this.disposeUnload?.()
    void this.wakeGuard.release()
  }

  private async runOne(entry: Tracked, ticket: UploadTicket): Promise<void> {
    const { item, controller } = entry
    const file = item.file

    if (!file) {
      this.update(item.id, { status: 'failed', error: 'Archivo no disponible.' })

      return
    }

    this.update(item.id, {
      status: 'uploading',
      assetId: ticket.asset_id,
      sessionId: ticket.session_id,
      strategy: ticket.strategy,
      totalParts: ticket.multipart?.total_parts ?? 1,
    })

    try {
      if (ticket.strategy === 'multipart') {
        await uploadMultipart(file, ticket, {
          transport: this.transport,
          projectId: this.projectId,
          signal: controller.signal,
          partConcurrency: this.partConcurrency,
          onProgress: (bytesSent) => this.update(item.id, { bytesSent }),
          onPartComplete: (completed) => this.update(item.id, { completedParts: completed }),
        })
      } else {
        await uploadSingle(file, ticket, {
          transport: this.transport,
          signal: controller.signal,
          onProgress: (bytesSent) => this.update(item.id, { bytesSent }),
        })
      }

      if (controller.signal.aborted) return

      this.update(item.id, { status: 'done', bytesSent: item.sizeBytes })
      void deleteSession(ticket.session_id)
    } catch (error) {
      if (controller.signal.aborted) return

      this.update(item.id, {
        status: 'failed',
        error: error instanceof Error ? error.message : 'Fallo al subir.',
      })
    }
  }

  /**
   * Protecciones de ciclo de vida mientras haya subidas activas.
   *
   * En móvil esto no es un adorno: los navegadores suspenden las pestañas en
   * segundo plano y cortan las peticiones en vuelo. Con subidas de dos horas
   * en Perú, va a ocurrir siempre.
   */
  private attachLifecycleGuards(): void {
    if (this.disposeUnload === null) {
      this.disposeUnload = warnBeforeUnload(() => this.hasActiveUploads())
    }

    if (isMobile()) {
      void this.wakeGuard.acquire()
    }

    this.disposeVisibility ??= onResumeFromBackground(() => {
      // Al volver, las partes en vuelo pueden haber muerto silenciosamente.
      // No hace falta hacer nada explícito: putWithProgress reintenta con
      // backoff y MultipartUploader arranca desde las partes ya persistidas.
      // Se re-pide el wake lock, que el sistema revoca al ocultar la pestaña.
      if (isMobile() && this.hasActiveUploads()) {
        void this.wakeGuard.acquire()
      }
    })
  }

  private releaseLifecycleGuardsIfIdle(): void {
    if (this.hasActiveUploads()) return

    void this.wakeGuard.release()
    this.disposeUnload?.()
    this.disposeUnload = null
  }

  private hasActiveUploads(): boolean {
    for (const { item } of this.tracked.values()) {
      if (item.status === 'uploading' || item.status === 'preparing' || item.status === 'finalizing') {
        return true
      }
    }

    return false
  }

  private update(itemId: string, patch: Partial<UploadItem>): void {
    const entry = this.tracked.get(itemId)
    if (!entry) return

    entry.item = { ...entry.item, ...patch }
    this.emit(entry.item)
  }

  private emit(item: UploadItem): void {
    this.events.onItemChange({ ...item })
  }
}
